/**
 * Système de tooltips contextuels pour l'onboarding
 * Prompt 6 - Guide l'utilisateur sur les fonctionnalités clés
 */

import { useEffect, useRef, useState, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useTooltips, TooltipId, TooltipConfig } from '@/hooks/useTooltips';
import { Button } from '@/components/ui/button';
import { X, Lightbulb } from 'lucide-react';

/**
 * Context pour partager l'état des tooltips
 */
interface TooltipContextValue {
  registerTrigger: (id: TooltipId, element: HTMLElement | null) => void;
  unregisterTrigger: (id: TooltipId) => void;
  triggerTooltip: (id: TooltipId) => void;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

/**
 * Hook pour utiliser le context des tooltips
 */
export function useTooltipContext() {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error('useTooltipContext must be used within TooltipSystem');
  }
  return context;
}

interface TooltipSystemProps {
  children: React.ReactNode;
}

/**
 * Provider pour le système de tooltips
 * Gère l'affichage des tooltips et leur positionnement
 */
export function TooltipSystem({ children }: TooltipSystemProps) {
  const { activeTooltip, hideTooltip, getTooltipConfig, showTooltip, isEnabled } = useTooltips();
  const [triggers, setTriggers] = useState<Map<TooltipId, HTMLElement>>(new Map());
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const config = activeTooltip ? getTooltipConfig(activeTooltip) : null;
  const triggerElement = activeTooltip ? triggers.get(activeTooltip) : null;

  // Calculer la position du tooltip
  useEffect(() => {
    if (!triggerElement || !config) return;

    const rect = triggerElement.getBoundingClientRect();
    const tooltipWidth = 320;
    const tooltipHeight = 150;
    const offset = 12;

    let top = 0;
    let left = 0;

    switch (config.position) {
      case 'top':
        top = rect.top - tooltipHeight - offset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'bottom':
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.left - tooltipWidth - offset;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeight / 2;
        left = rect.right + offset;
        break;
      default:
        top = rect.bottom + offset;
        left = rect.left + rect.width / 2 - tooltipWidth / 2;
    }

    // Garder le tooltip dans les limites de l'écran
    top = Math.max(10, Math.min(top, window.innerHeight - tooltipHeight - 10));
    left = Math.max(10, Math.min(left, window.innerWidth - tooltipWidth - 10));

    setTooltipPosition({ top, left });
  }, [triggerElement, config]);

  // Fermer avec Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeTooltip) {
        hideTooltip();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTooltip, hideTooltip]);

  // Fonctions du context
  const registerTrigger = (id: TooltipId, element: HTMLElement | null) => {
    if (element) {
      setTriggers(prev => new Map(prev).set(id, element));
    }
  };

  const unregisterTrigger = (id: TooltipId) => {
    setTriggers(prev => {
      const newMap = new Map(prev);
      newMap.delete(id);
      return newMap;
    });
  };

  const triggerTooltip = (id: TooltipId) => {
    if (isEnabled) {
      showTooltip(id);
    }
  };

  const contextValue: TooltipContextValue = {
    registerTrigger,
    unregisterTrigger,
    triggerTooltip,
  };

  return (
    <TooltipContext.Provider value={contextValue}>
      {children}

      {/* Overlay et Tooltip */}
      {config && createPortal(
        <>
          {/* Overlay semi-transparent */}
          <div
            className="fixed inset-0 z-[100] bg-black/30 backdrop-blur-sm"
            onClick={hideTooltip}
          />

          {/* Highlight sur l'élément trigger */}
          {triggerElement && (
            <div
              className="fixed z-[101] ring-4 ring-primary ring-offset-2 ring-offset-background rounded-lg pointer-events-none"
              style={{
                top: triggerElement.getBoundingClientRect().top - 4,
                left: triggerElement.getBoundingClientRect().left - 4,
                width: triggerElement.getBoundingClientRect().width + 8,
                height: triggerElement.getBoundingClientRect().height + 8,
              }}
            />
          )}

          {/* Tooltip card */}
          <div
            className="fixed z-[102] w-80 animate-in fade-in-0 zoom-in-95 duration-200"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
            }}
          >
            <div className="bg-card border border-border rounded-xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-primary/10 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-primary" />
                  <span className="text-xs font-medium text-primary uppercase tracking-wide">
                    Astuce
                  </span>
                </div>
                <button
                  onClick={hideTooltip}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                <h4 className="font-semibold text-foreground text-base mb-1">
                  {config.title}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {config.content}
                </p>
              </div>

              {/* Footer */}
              <div className="px-4 pb-4">
                <Button
                  onClick={hideTooltip}
                  size="sm"
                  className="w-full"
                >
                  Compris !
                </Button>
              </div>
            </div>

            {/* Arrow pointer */}
            {config.position && (
              <TooltipArrow position={config.position} />
            )}
          </div>
        </>,
        document.body
      )}
    </TooltipContext.Provider>
  );
}

/**
 * Flèche du tooltip
 */
function TooltipArrow({ position }: { position: 'top' | 'bottom' | 'left' | 'right' }) {
  const baseClasses = "absolute w-3 h-3 bg-card border-border transform rotate-45";

  switch (position) {
    case 'top':
      return <div className={`${baseClasses} border-b border-r -bottom-1.5 left-1/2 -translate-x-1/2`} />;
    case 'bottom':
      return <div className={`${baseClasses} border-t border-l -top-1.5 left-1/2 -translate-x-1/2`} />;
    case 'left':
      return <div className={`${baseClasses} border-t border-r -right-1.5 top-1/2 -translate-y-1/2`} />;
    case 'right':
      return <div className={`${baseClasses} border-b border-l -left-1.5 top-1/2 -translate-y-1/2`} />;
    default:
      return null;
  }
}

/**
 * Composant pour wrapper un élément et déclencher un tooltip
 */
interface TooltipTriggerProps {
  tooltipId: TooltipId;
  children: React.ReactNode;
  showOnMount?: boolean;
  delay?: number;
  className?: string;
}

export function TooltipTrigger({
  tooltipId,
  children,
  showOnMount = false,
  delay = 500,
  className = '',
}: TooltipTriggerProps) {
  const ref = useRef<HTMLDivElement>(null);
  const context = useContext(TooltipContext);

  // Stocker les valeurs dans des refs pour éviter les re-renders
  const tooltipIdRef = useRef(tooltipId);
  const showOnMountRef = useRef(showOnMount);
  const delayRef = useRef(delay);
  const hasRegistered = useRef(false);
  const hasShown = useRef(false);

  // S'enregistrer auprès du context (une seule fois)
  useEffect(() => {
    if (hasRegistered.current) return;
    if (context && ref.current) {
      hasRegistered.current = true;
      context.registerTrigger(tooltipIdRef.current, ref.current);
      return () => {
        context.unregisterTrigger(tooltipIdRef.current);
      };
    }
  }, [context]);

  // Afficher automatiquement au mount si demandé
  useEffect(() => {
    if (hasShown.current) return;
    if (!showOnMountRef.current || !context) return;

    hasShown.current = true;
    const timer = setTimeout(() => {
      context.triggerTooltip(tooltipIdRef.current);
    }, delayRef.current);
    return () => clearTimeout(timer);
  }, [context]);

  return (
    <div ref={ref} data-tooltip-id={tooltipId} className={className}>
      {children}
    </div>
  );
}

export default TooltipSystem;
